import logger from '../../lib/logger';
import React, { useState } from 'react';
import { Clock, Upload, FileSpreadsheet, Plus, Trash2, Check, Loader2, X } from 'lucide-react';
import { Button } from '../ui/button';

const PrayerTimesTab = ({ token, apiBaseUrl, currentPrayer, prayerStats: initialPrayerStats, onDashboardRefresh }) => {
  const [prayerTimesData, setPrayerTimesData] = useState([]);
  const [loadingPrayerTimes, setLoadingPrayerTimes] = useState(false);
  const [prayerTimesModified, setPrayerTimesModified] = useState(false);
  const [savingPrayerTimes, setSavingPrayerTimes] = useState(false);
  const [pasteData, setPasteData] = useState('');
  const [showPasteModal, setShowPasteModal] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [prayerStats, setPrayerStats] = useState(initialPrayerStats);
  const [loaded, setLoaded] = useState(false);

  const loadData = async () => {
    setLoadingPrayerTimes(true);
    try {
      const response = await fetch(`${apiBaseUrl}/api/admin/prayer-times/all`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (!response.ok) throw new Error('Failed to fetch prayer times');
      const data = await response.json();
      setPrayerTimesData(data);
      setPrayerTimesModified(false);
      const statsRes = await fetch(`${apiBaseUrl}/api/prayer-times/stats`);
      if (statsRes.ok) setPrayerStats(await statsRes.json());
    } catch (err) {
      logger.error('Failed to load prayer times:', err);
      setUploadError('Failed to load prayer times: ' + err.message);
    } finally { setLoadingPrayerTimes(false); }
  };

  // Load data on first render
  if (!loaded) { setLoaded(true); loadData(); }

  const updateField = (index, field, value) => {
    const newData = [...prayerTimesData];
    newData[index] = { ...newData[index], [field]: value };
    setPrayerTimesData(newData);
    setPrayerTimesModified(true);
  };

  const addRow = () => {
    const today = new Date();
    const lastDate = prayerTimesData.length > 0 ? new Date(prayerTimesData[prayerTimesData.length - 1].gregorian_date) : today;
    lastDate.setDate(lastDate.getDate() + 1);
    const newDate = lastDate.toISOString().split('T')[0];
    setPrayerTimesData([...prayerTimesData, { gregorian_date: newDate, hijri_date: '', fajr: '', sunrise: '', dhuhr: '', sunset: '', maghrib: '' }]);
    setPrayerTimesModified(true);
  };

  const deleteRow = async (index) => {
    const item = prayerTimesData[index];
    if (window.confirm(`Delete prayer times for ${item.gregorian_date}?`)) {
      try {
        await fetch(`${apiBaseUrl}/api/admin/prayer-times/${item.gregorian_date}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
        setPrayerTimesData(prayerTimesData.filter((_, i) => i !== index));
        onDashboardRefresh();
      } catch (err) { logger.error('Failed to delete:', err); }
    }
  };

  const save = async () => {
    setSavingPrayerTimes(true); setUploadError(null); setUploadResult(null);
    try {
      const cleanedData = prayerTimesData.map(pt => ({
        gregorian_date: String(pt.gregorian_date || '').trim(),
        hijri_date: String(pt.hijri_date || '').trim(),
        fajr: String(pt.fajr || '').trim(),
        sunrise: String(pt.sunrise || '').trim(),
        dhuhr: String(pt.dhuhr || '').trim(),
        sunset: String(pt.sunset || '').trim(),
        maghrib: String(pt.maghrib || '').trim()
      })).filter(pt => pt.gregorian_date);
      if (cleanedData.length === 0) { setUploadError('No valid prayer times to save.'); return; }
      const response = await fetch(`${apiBaseUrl}/api/admin/prayer-times/bulk-save`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }, body: JSON.stringify(cleanedData)
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.detail || 'Failed to save');
      setUploadResult({ records_imported: result.saved, records_updated: result.updated });
      setPrayerTimesModified(false);
      onDashboardRefresh(); loadData();
    } catch (err) { setUploadError('Failed to save: ' + err.message); }
    finally { setSavingPrayerTimes(false); }
  };

  const deleteSelected = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Delete ${selectedRows.length} selected entries?`)) return;
    setDeleting(true);
    try {
      for (const date of selectedRows) {
        await fetch(`${apiBaseUrl}/api/admin/prayer-times/${date}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      }
      setSelectedRows([]); loadData(); onDashboardRefresh();
    } catch (err) { setUploadError('Failed to delete: ' + err.message); }
    finally { setDeleting(false); }
  };

  const deleteAll = async () => {
    if (!window.confirm('Delete ALL prayer times? This cannot be undone.')) return;
    setDeleting(true);
    try {
      for (const date of prayerTimesData.map(pt => pt.gregorian_date)) {
        await fetch(`${apiBaseUrl}/api/admin/prayer-times/${date}`, { method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` } });
      }
      loadData(); onDashboardRefresh();
    } catch (err) { setUploadError('Failed to delete: ' + err.message); }
    finally { setDeleting(false); }
  };

  const toggleRow = (date) => { setSelectedRows(prev => prev.includes(date) ? prev.filter(d => d !== date) : [...prev, date]); };
  const toggleSelectAll = () => { setSelectedRows(selectedRows.length === prayerTimesData.length ? [] : prayerTimesData.map(pt => pt.gregorian_date)); };

  const parsePastedData = () => {
    if (!pasteData.trim()) return;
    const lines = pasteData.trim().split('\n');
    const parsedData = [];
    for (const line of lines) {
      const cols = line.split(/\t+|\s{2,}/).map(col => col.trim());
      if (cols.length >= 7) {
        let gregorianDate = cols[0].trim();
        try { const d = new Date(gregorianDate); if (!isNaN(d.getTime())) gregorianDate = d.toISOString().split('T')[0]; } catch (e) { logger.warn('Date parse error:', e); }
        if (gregorianDate && gregorianDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
          parsedData.push({ gregorian_date: gregorianDate, hijri_date: String(cols[1] || '').trim(), fajr: String(cols[2] || '').trim(), sunrise: String(cols[3] || '').trim(), dhuhr: String(cols[4] || '').trim(), sunset: String(cols[5] || '').trim(), maghrib: String(cols[6] || '').trim() });
        }
      }
    }
    if (parsedData.length > 0) {
      const mergedData = [...prayerTimesData];
      for (const newItem of parsedData) {
        const idx = mergedData.findIndex(item => item.gregorian_date === newItem.gregorian_date);
        if (idx >= 0) mergedData[idx] = newItem; else mergedData.push(newItem);
      }
      mergedData.sort((a, b) => a.gregorian_date.localeCompare(b.gregorian_date));
      setPrayerTimesData(mergedData);
      setPrayerTimesModified(true);
      setShowPasteModal(false); setPasteData('');
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) { setUploadError('Please upload an Excel file'); return; }
    setUploading(true); setUploadError(null); setUploadResult(null);
    try {
      const formData = new FormData(); formData.append('file', file);
      const response = await fetch(`${apiBaseUrl}/api/admin/prayer-times/upload`, { method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, body: formData });
      if (!response.ok) { const error = await response.json(); throw new Error(error.detail || 'Upload failed'); }
      setUploadResult(await response.json());
      const statsRes = await fetch(`${apiBaseUrl}/api/prayer-times/stats`);
      if (statsRes.ok) setPrayerStats(await statsRes.json());
    } catch (err) { setUploadError(err.message); }
    finally { setUploading(false); e.target.value = ''; }
  };

  return (
    <div className="bg-white shadow-lg">
      <div className="p-6 border-b flex justify-between items-center">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 border border-[#c5a572] flex items-center justify-center"><Clock className="w-6 h-6 text-[#c5a572]" /></div>
          <div><h2 className="text-xl text-[#1a2f23]">Prayer Times Management</h2><p className="text-sm text-[#5a6d62]">View, edit, and manage prayer times for your website</p></div>
        </div>
      </div>

      {currentPrayer && (
        <div className="bg-[#f8f6f1] p-4 border-b">
          <p className="text-xs text-[#5a6d62] mb-2">Currently showing on website ({currentPrayer.gregorian_date})</p>
          <div className="flex flex-wrap gap-6">
            {['fajr', 'sunrise', 'dhuhr', 'sunset', 'maghrib'].map(p => (
              <div key={p}><span className="text-xs text-[#c5a572] uppercase">{p}:</span> <span className="text-[#1a2f23] font-medium ml-1">{currentPrayer[p]}</span></div>
            ))}
          </div>
        </div>
      )}

      <div className="px-6 py-3 bg-gray-50 border-b flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-4">
          {prayerStats && <p className="text-sm text-[#5a6d62]"><strong className="text-[#1a2f23]">{prayerStats.total_records}</strong> days in database {prayerStats.earliest_date && <span className="text-xs">({prayerStats.earliest_date} to {prayerStats.latest_date})</span>}</p>}
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setShowPasteModal(true)} variant="outline" className="border-[#c5a572] text-[#c5a572]"><FileSpreadsheet className="w-4 h-4" /> Paste from Excel</Button>
          <Button onClick={addRow} variant="outline"><Plus className="w-4 h-4" /> Add Row</Button>
          {selectedRows.length > 0 && <Button onClick={deleteSelected} variant="outline" className="border-red-400 text-red-600 hover:bg-red-50" disabled={deleting}><Trash2 className="w-4 h-4" /> Delete Selected ({selectedRows.length})</Button>}
          {prayerTimesData.length > 0 && <Button onClick={deleteAll} variant="outline" className="border-red-400 text-red-600 hover:bg-red-50" disabled={deleting}><Trash2 className="w-4 h-4" /> Delete All</Button>}
          <Button onClick={save} className="bg-green-600 hover:bg-green-700 text-white" disabled={!prayerTimesModified || savingPrayerTimes}>
            {savingPrayerTimes ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : <><Check className="w-4 h-4" /> Save & Publish</>}
          </Button>
        </div>
      </div>

      {uploadResult && <div className="px-6 py-3 bg-green-50 border-b border-green-200"><p className="text-sm text-green-700">Saved {uploadResult.records_imported} new, updated {uploadResult.records_updated} existing records.</p></div>}
      {uploadError && <div className="px-6 py-3 bg-red-50 border-b border-red-200"><p className="text-sm text-red-700">{uploadError}</p></div>}
      {prayerTimesModified && <div className="px-6 py-3 bg-amber-50 border-b border-amber-200"><p className="text-sm text-amber-700">You have unsaved changes. Click &quot;Save &amp; Publish&quot; to update the website.</p></div>}

      <div className="overflow-x-auto" style={{maxHeight: '500px', overflowY: 'auto'}}>
        {loadingPrayerTimes ? (
          <div className="p-12 text-center"><Loader2 className="w-8 h-8 text-[#c5a572] animate-spin mx-auto" /><p className="text-sm text-[#5a6d62] mt-2">Loading prayer times...</p></div>
        ) : prayerTimesData.length === 0 ? (
          <div className="p-12 text-center">
            <Clock className="w-12 h-12 text-[#c5a572]/50 mx-auto mb-4" />
            <p className="text-[#5a6d62] mb-4">No prayer times in database.</p>
            <div className="flex gap-3 justify-center">
              <Button onClick={() => setShowPasteModal(true)} className="btn-primary"><FileSpreadsheet className="w-4 h-4" /> Paste from Excel</Button>
              <Button onClick={() => document.getElementById('excel-upload').click()} variant="outline"><Upload className="w-4 h-4" /> Upload Excel File</Button>
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#1a2f23] text-white sticky top-0">
              <tr>
                <th className="px-3 py-3 text-center font-medium w-12"><input type="checkbox" checked={selectedRows.length === prayerTimesData.length && prayerTimesData.length > 0} onChange={toggleSelectAll} className="w-4 h-4 rounded" /></th>
                <th className="px-3 py-3 text-left font-medium">Gregorian Date</th>
                <th className="px-3 py-3 text-left font-medium">Hijri Date</th>
                <th className="px-3 py-3 text-center font-medium">Fajr</th>
                <th className="px-3 py-3 text-center font-medium">Sunrise</th>
                <th className="px-3 py-3 text-center font-medium">Dhuhr</th>
                <th className="px-3 py-3 text-center font-medium">Sunset</th>
                <th className="px-3 py-3 text-center font-medium">Maghrib</th>
                <th className="px-3 py-3 text-center font-medium w-16">Delete</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {prayerTimesData.map((pt, index) => (
                <tr key={pt.gregorian_date || index} className={`hover:bg-[#f8f6f1] ${selectedRows.includes(pt.gregorian_date) ? 'bg-blue-50' : ''}`}>
                  <td className="px-3 py-2 text-center"><input type="checkbox" checked={selectedRows.includes(pt.gregorian_date)} onChange={() => toggleRow(pt.gregorian_date)} className="w-4 h-4 rounded" /></td>
                  <td className="px-3 py-2"><input type="date" value={pt.gregorian_date || ''} onChange={(e) => updateField(index, 'gregorian_date', e.target.value)} className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:border-[#c5a572] focus:outline-none" /></td>
                  <td className="px-3 py-2"><input type="text" value={pt.hijri_date || ''} onChange={(e) => updateField(index, 'hijri_date', e.target.value)} className="w-full px-2 py-1 border border-gray-200 rounded text-sm focus:border-[#c5a572] focus:outline-none" placeholder="e.g., 1 Muharram 1447" /></td>
                  {['fajr', 'sunrise', 'dhuhr', 'sunset', 'maghrib'].map(field => (
                    <td key={field} className="px-3 py-2"><input type="text" value={pt[field] || ''} onChange={(e) => updateField(index, field, e.target.value)} className="w-20 px-2 py-1 border border-gray-200 rounded text-sm text-center focus:border-[#c5a572] focus:outline-none" /></td>
                  ))}
                  <td className="px-3 py-2 text-center"><button onClick={() => deleteRow(index)} className="text-red-500 hover:text-red-700 p-1"><Trash2 className="w-4 h-4" /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="p-6 border-t bg-[#f8f6f1]">
        <p className="text-sm text-[#5a6d62] mb-3">Alternative: Upload an Excel file directly</p>
        <input type="file" id="excel-upload" accept=".xlsx,.xls" onChange={handleFileUpload} className="hidden" />
        <Button onClick={() => document.getElementById('excel-upload').click()} variant="outline" disabled={uploading}>
          {uploading ? <><Loader2 className="w-4 h-4 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4" /> Upload Excel File</>}
        </Button>
      </div>

      {showPasteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-lg font-medium text-[#1a2f23]">Paste Prayer Times from Excel</h3>
              <button onClick={() => setShowPasteModal(false)} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6">
              <p className="text-sm text-[#5a6d62] mb-2">Copy your Excel data and paste it below. Expected columns (tab-separated):</p>
              <p className="text-xs bg-gray-100 p-2 rounded font-mono mb-4">Gregorian Date | Hijri Date | Fajr | Sunrise | Dhuhr | Sunset | Maghrib</p>
              <p className="text-xs text-amber-600 mb-3">If dates already exist, they will be overwritten with new values.</p>
              <textarea value={pasteData} onChange={(e) => setPasteData(e.target.value)} className="w-full h-64 p-3 border border-gray-300 rounded font-mono text-sm focus:border-[#c5a572] focus:outline-none" placeholder="Paste your Excel data here..." />
            </div>
            <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
              <Button onClick={() => setShowPasteModal(false)} variant="outline">Cancel</Button>
              <Button onClick={parsePastedData} className="bg-[#c5a572] hover:bg-[#b39562] text-white" disabled={!pasteData.trim()}><Check className="w-4 h-4" /> Import & Merge Data</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PrayerTimesTab;
